import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoDomeWall } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a containment dome wall, which has a cylindrical shape
 * similar to a vertical tank shell but without the additional features.
 *
 * @param model       The containment dome wall component model
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const DomeWallSurface3D: React.FC<
  {
    model: GeckoDomeWall
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const bounds = model.componentBounds
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num,
            model.componentBounds.height.num,
            model.radius.num
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )
    return { bounds, scale }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry
  }, [show3D])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}
    />
  )
}
